import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { TextBlockCategory } from "Enums";

/**
 * Control for displaying checkboxes.
 *
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Checkbox with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class Checkbox extends Control {
    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<input type="checkbox" class="k-checkbox k-checkbox-md" />`;

    /**
     * @hidden
     */
    protected bindingProperty: string = "checked";

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-checkbox";
    
    /**
     * @hidden
     */
    protected isBlockElement = false;

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        if (this.jQueryElement.prop("readonly")) {
            this.jQueryElement.prop("disabled", true);
        }
        if (this.jQueryElement.hasClass("k-disabled")) {
            this.jQueryElement.on("click", (e) => {
                e.preventDefault();
                this.jQueryElement.trigger("change");
            });
        }
        this.jQueryElement.on("change", () => this.trigger(ControlEventTypes.Change));
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
    }

    /**
     * Gets template for tooltip
     * @param bindingField
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return `#= ${bindingField} == "true" || ${bindingField} == true ? "${this.textBlockService.get(
            "True",
            TextBlockCategory.global,
            true
        )}" : "${this.textBlockService.get("False", TextBlockCategory.global, true)}" #`;
    }

    /**
     * Get value from the control. The value is true when the checkbox is checked, otherwise false.
     * @since 4.0.0
     */
    public getValue(): boolean {
        if (this.jQueryElement.is(":checked") === true) {
            return true;
        } else if (this.jQueryElement.is(":checked") === false) {
            return false;
        }
    }

    /**
     * Set control value. Set true if the checkbox should be checked, otherwise false.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: boolean) {
        let oldValue = this.jQueryElement.is(":checked");
        if (oldValue != value) {
            this.jQueryElement.prop("checked", value);
            this.jQueryElement.trigger("change");
        }
    }

    /**
     * Gets the display template for readonly columns and non-editable grid.
     * @hidden
     */
    public getDisplayTemplate(bindingField?: string): string {
        let field = (bindingField || "").split(`"`).join("&quot;");
        return `<input type="checkbox" data-dms-checkboxid="${this.controlDefinition.ControlID}" class="k-checkbox k-checkbox-md dms-checkbox-label k-disabled" onclick="return false" #= (${field} ? 'checked="checked"' : '') # />`;
    }

    /**
     * Get the template for the grid group header.
     * @hidden
     */
    public getGroupHeaderTemplate(): string {
        return this.getTooltipTemplate("value");
    }

    /**
     * Get the display template for a binding field.
     * @hidden
     */
    public getDisplayTemplateForField(bindingField: string): string {
        return this.getTooltipTemplate(bindingField);
    }

    /**
     * Gets the datatype for the ViewModel (ie. "string", "number", etc.)
     * @hidden
     */
    public getModelDatatype(): string {
        return "boolean";
    }

    /**
     * Gets the default value for the model if no default value is set in controlDefinition.
     * @hidden
     */
    public getModelDefaultValue(): any {
        return false;
    }

    /**
     * Dispose the control.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();

        if (this.jQueryElement != null) {
            this.jQueryElement.off("change");
            this.jQueryElement.off("focusout");
        }
    }
}
